// StagingInterfacePowerplants.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import useLoadGoogleMaps from "./useLoadGoogleMaps";
import Papa from "papaparse";
import axios from "axios";
import "./StagingInterface.css";

const LIBRARIES = [];
const mapContainerStyle = { width: "100%", height: "100%" };

// ---------- API helper ----------
const createApiInstance = (token, backendKey) => {
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(backendKey ? { "x-api-key": backendKey } : {}),
      "Content-Type": "application/json",
    },
    withCredentials: true,
  });
};

// ---------- Validation ----------
const validateRow = (row) => {
    const errors = [];
    const requiredFields = ["name", "country_long"];
    const numericFields = ["capacity_mw", "longitude", "latitude"];

    // required text fields
    requiredFields.forEach((f) => {
      if (row[f] == null || String(row[f]).trim() === "") {
        errors.push(`Missing value for "${f}"`);
      }
    });

    // numeric fields: only check if non-blank
    numericFields.forEach((f) => {
      if (row[f] != null && String(row[f]).trim() !== "" && isNaN(parseFloat(row[f]))) {
        errors.push(`"${f}" must be a valid number`);
      }
    });

    // coordinate range (only when provided)
    if (row.longitude != null && String(row.longitude).trim() !== "") {
      const lon = Number(row.longitude);
      if (!Number.isFinite(lon) || lon < -180 || lon > 180)
        errors.push(`"longitude" must be between -180 and 180`);
    }
    if (row.latitude != null && String(row.latitude).trim() !== "") {
      const lat = Number(row.latitude);
      if (!Number.isFinite(lat) || lat < -90 || lat > 90)
        errors.push(`"latitude" must be between -90 and 90`);
    }

    return errors;
  };


// ---------- ID Generator ----------
const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const StagingInterfacePowerplants = ({ accessToken }) => {
  const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
  const BACKEND_KEY = process.env.REACT_APP_BACKEND_KEY;
  const api = useMemo(() => createApiInstance(accessToken, BACKEND_KEY), [accessToken, BACKEND_KEY]);

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [existingPlants, setExistingPlants] = useState([]);
  const [uploadedPlants, setUploadedPlants] = useState([]);
  const [errors, setErrors] = useState([]);
  const [status, setStatus] = useState("");

  const [selectedPlant, setSelectedPlant] = useState(null);
  const [userCenter, setUserCenter] = useState({ lat: 20, lng: 0 });
  const [zoom, setZoom] = useState(2.5);
  const fileRef = useRef(null);

  const { loaded: mapsLoaded, error: mapsError } = useLoadGoogleMaps({ apiKey: API_KEY, libraries: LIBRARIES });

  // ---- Load available countries ----
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/power-plants/countries");
        if (!alive) return;
        setCountries(res.data || []);
      } catch (err) {
        console.error("Error fetching countries:", err);
      }
    })();
    return () => { alive = false; };
  }, [api]);

  // ---- Load power plants for selected country ----
  useEffect(() => {
    if (!selectedCountry) {
      setExistingPlants([]);
      return;
    }
    let alive = true;
    (async () => {
      try {
        setStatus(`Loading power plants for ${selectedCountry}...`);
        const res = await api.get(`/power-plants/country?country=${encodeURIComponent(selectedCountry)}`);
        if (!alive) return;
        const data = (res.data || []).map((p) => ({
          ...p,
          longitude: Number(p.longitude),
          latitude: Number(p.latitude),
        }));
        setExistingPlants(data);
        setStatus(`Loaded ${data.length} power plants for ${selectedCountry}.`);
        if (data.length > 0) {
          const avgLat = data.reduce((s, p) => s + p.latitude, 0) / data.length;
          const avgLng = data.reduce((s, p) => s + p.longitude, 0) / data.length;
          setUserCenter({ lat: avgLat, lng: avgLng });
          setZoom(5);
        }
      } catch (err) {
        console.error("Error fetching power plants:", err);
        setStatus("Error loading power plants.");
      }
    })();
    return () => { alive = false; };
  }, [api, selectedCountry]);

  // ---- CSV upload (validation only, no backend insert) ----
  const resetFile = () => { if (fileRef.current) fileRef.current.value = ""; };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data.map((r) => ({
          staging_id: genId(),
          name: r.name?.trim() ?? "",
          country_long: r.country_long?.trim() ?? "",
          primary_fuel: r.primary_fuel?.trim() ?? "",
          // allow blanks; convert only if non-empty
          capacity_mw:
            r.capacity_mw != null && String(r.capacity_mw).trim() !== ""
              ? parseFloat(r.capacity_mw)
              : "",
          longitude:
            r.longitude != null && String(r.longitude).trim() !== ""
              ? Number(r.longitude)
              : "",
          latitude:
            r.latitude != null && String(r.latitude).trim() !== ""
              ? Number(r.latitude)
              : "",
        }));

        const allErrors = rows.flatMap((row, i) =>
          validateRow(row).map((msg) => `Row ${i + 1}: ${msg}`)
        );

        if (allErrors.length > 0) {
          setErrors(allErrors);
          setStatus("Validation errors found. The file has been cleared.");
          setUploadedPlants([]);
          resetFile();
          return;
        }

        setUploadedPlants(rows);
        setErrors([]);
        setStatus(`Loaded ${rows.length} uploaded power plant(s).`);
        resetFile();
      },
      error: (err) => {
        setErrors([`CSV parsing error: ${err.message}`]);
        setStatus("CSV parsing error; the file has been cleared.");
        resetFile();
      },
    });
  };

    // ---- Icon helper ----
    const plantIcon = (type = "existing") => {
        const url =
        type === "uploaded"
            ? "http://maps.google.com/mapfiles/ms/icons/red-dot.png" // standard Google red marker
            : "icons/dc_existing.png"; // your default icon for existing plants
        return window.google?.maps
        ? { url, scaledSize: new window.google.maps.Size(20, 20) }
        : undefined;
    };


  return (
    <div style={{ width: "100%", height: "100vh" }}>
      <a href="/" className="btn btn--ghost home-fab">← Home</a>

      <div className="staging-toolbar">
      <div
        className="staging-toolbar__left"
        style={{ display: "flex", justifyContent: "flex-start", marginLeft: "180px" }}
        >
        <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            style={{
            padding: "4px 8px",
            minWidth: 70,
            borderRadius: "6px",
            cursor: "pointer",
            }}
        >
            <option value="">-- Select Country --</option>
            {countries.map((c) => (
            <option key={c} value={c}>{c}</option>
            ))}
        </select>
        </div>

        <div className="staging-toolbar__center">
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} />
        </div>
        <div className="staging-toolbar__right">
          <button className="btn btn--ghost" onClick={() => {
            setUploadedPlants([]); setErrors([]); setStatus(""); resetFile();
          }}>
            Clear uploaded
          </button>
        </div>
      </div>

      {status && <div className="staging-status">{status}</div>}
      {errors.length > 0 && (
        <div className="staging-errors" role="region" aria-label="Validation errors">
          <strong>Validation errors:</strong>
          <ul style={{ margin: "4px 0 0 18px" }}>
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {!mapsLoaded && !mapsError && (
        <div style={{ padding: 16 }}>Loading map...</div>
      )}
      {mapsError && (
        <div role="alert" style={{ padding: 16, color: "#b00020" }}>
          Error loading Google Maps: {mapsError.message}
        </div>
      )}

      {mapsLoaded && (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={userCenter}
          zoom={zoom}
          onClick={() => setSelectedPlant(null)}
          options={{
            minZoom: 2,
            maxZoom: 24,
            restriction: {
              latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
              strictBounds: false,
            },
          }}
        >
            {/* Existing power plants from backend */}
            {existingPlants.map((p) => (
            <Marker
                key={`existing-${p.id}`}
                position={{ lat: p.latitude, lng: p.longitude }}
                icon={plantIcon("existing")}
                onClick={() => setSelectedPlant(p)}
                zIndex={1}
            />
            ))}

            {/* Uploaded CSV plants (temporary, local only) */}
            {uploadedPlants.map((p) => (
            <Marker
                key={`uploaded-${p.staging_id}`}
                position={{ lat: p.latitude, lng: p.longitude }}
                icon={plantIcon("uploaded")}
                onClick={() => setSelectedPlant(p)}
                zIndex={2}
            />
            ))}


          {/* InfoWindow for selected plant */}
          {selectedPlant && (
            <InfoWindow
              position={{ lat: Number(selectedPlant.latitude), lng: Number(selectedPlant.longitude) }}
              onCloseClick={() => setSelectedPlant(null)}
            >
              <div>
                <h5>{selectedPlant.name}</h5>
                <ul>
                  {selectedPlant.country_long && <li><strong>Country:</strong> {selectedPlant.country_long}</li>}
                  {selectedPlant.primary_fuel && <li><strong>Fuel:</strong> {selectedPlant.primary_fuel}</li>}
                  {selectedPlant.capacity_mw && <li><strong>Capacity:</strong> {selectedPlant.capacity_mw} MW</li>}
                  <li><strong>Coordinates:</strong> {selectedPlant.latitude}, {selectedPlant.longitude}</li>
                </ul>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </div>
  );
};

export default StagingInterfacePowerplants;
